import { getVisibleOverview } from "src/components/PositionOverviewTable";
import {
  aggregatedStatusColours,
  headerCell,
  remainingDays,
  statusCell,
  worstStatusFirst,
  type ExcelRow,
  type ExcelSheet,
  type ExportedStatus,
} from "src/utils/excelExport";

import type { OperationWithMetadata, UserRow } from ".";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface PositionOverviewFilters {
  searchUsers: number[];
  searchRoles: number[];
  searchPositions: number[];
  searchOperations: number[];
}

// Mirrors PositionOverviewTable: one row per user, each position a merged header over its operation columns
export const buildPositionOverviewSheet = (
  positions_: OperationWithMetadata[][],
  users_: UserRow[],
  filters: PositionOverviewFilters,
  t: Translate,
): ExcelSheet => {
  const { positions, users, opIndexes } = getVisibleOverview({ positions: positions_, users: users_, ...filters }) as {
    positions: OperationWithMetadata[][];
    users: UserRow[];
    opIndexes: Record<number, boolean> | null;
  };

  const headerRows: ExcelRow[] = [
    [
      headerCell(t("user"), { rowSpan: 2 }),
      ...positions.flatMap((position) => [
        headerCell(position[0].position_name, { columnSpan: position.length, align: "center" }),
        ...position.slice(1).map(() => null),
      ]),
    ],
    [
      null,
      // The position-level column has no operation, so its header stays empty like on screen
      ...positions.flatMap((position) =>
        position.map((op) => headerCell(op.is_position_level ? "" : op.operation_name)),
      ),
    ],
  ];

  const statusesOf = (user: UserRow): ExportedStatus[] =>
    user.positions
      .slice(1)
      .map((status, index) => ({
        cell: statusCell(status, t, remainingDays(user.validUntil[index])),
        validUntil: user.validUntil[index],
      }))
      .filter((_status, index) => !opIndexes || opIndexes[index]);

  // The users with the worst status come first. The user name has the colour of the user's worst status.
  const bodyRows: ExcelRow[] = worstStatusFirst(users, statusesOf).map((user): ExcelRow => {
    const statusCells = statusesOf(user).map(({ cell }) => cell);
    return [{ value: user.positions[0], ...aggregatedStatusColours(statusCells) }, ...statusCells];
  });

  return {
    headerRows,
    bodyRows,
    columnWidths: [30, ...positions.flatMap((position) => position.map(() => 14))],
  };
};
